import { useEffect, useRef, useState, TransitionEvent } from 'react';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '@/state/store';
import { closeProfile, setIsMyPrizesOpen, setIsShopOpen } from '@/state/features/app';
import { getArisewareScoresForUser, getAvailableTitles, getTitles } from '@/arise/api';
import { useAudio } from '@/hooks/audio';
import { useTheme } from '@/theme';
import { Title } from '@/models/profile';
import IconButton from '@/components/IconButton';
import Icon from '@/components/Icon';
import Button from '@/components/Forms/Button';
import LoadingScreen from '@/components/LoadingScreen';
import Badge from '@/components/Badge';
import ThemedMenuBar from '@/components/ThemedMenuBar';
import DisplayNameWithBadges from '@/components/DisplayNameWithBadges';
import AvatarLayered from '@/components/AvatarLayered';
import Rank from '@/components/Rank';
import MyPrizesDrawer from '@/components/MyPrizesDrawer';
import { processTitles } from '@/utils/profile';
import { sendEvent, sendEventV2 } from '@/utils/analytics';
import { ArisewareScore } from '@/models/misc';

import ProfileCharacter from './ProfileCharacter';
import ProfileBackground from './ProfileBackground';
import ProfileStatsSummary, { ProfileStatSummaryItem } from './ProfileStatsSummary';
import ProfileEditor from './ProfileEditor';
import ArisewareScores from './ArisewareScores';

import * as styles from './styles.module.scss';

export default function ProfileDrawer() {
    const dispatch = useAppDispatch();
    const drawerRef = useRef<HTMLDivElement>(null);
    const { audio } = useAudio();
    const theme = useTheme();
    const { isProfileOpen, currentlyViewingProfile, isMyPrizesOpen } = useAppSelector((state) => state.app);
    const currentUserProfile = useAppSelector((state) => state.profile.currentUserProfile);
    const isViewingOwnProfile = currentlyViewingProfile?.userID === currentUserProfile?.userID;
    const prizesBackgroundImage = theme.customJSON?.profileBackground;

    const [previousUserID, setPreviousUserID] = useState(null);
    const [arisewareScores, setArisewareScores] = useState<ArisewareScore[]>(null);
    const [titles, setTitles] = useState(null);
    const [numberOfAvailableTitles, setNumberOfAvailableTitles] = useState(null);
    const [characterIsLoaded, setCharacterIsLoaded] = useState(false);
    const [backgroundIsLoaded, setBackgroundIsLoaded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const isFirstTimeOpened = useRef(true);

    // If viewing own profile, always show the latest profile data. Otherwise use data contained in message.
    const profile = isViewingOwnProfile ? currentUserProfile : currentlyViewingProfile;

    const customJsonFeatures = theme.customJSON.profileFeaturesEnabled;
    const featuresEnabled = {
        character: customJsonFeatures?.character !== false,
        background: customJsonFeatures?.background !== false,
        layeredAvatar: customJsonFeatures?.layeredAvatar !== false,
        faction: customJsonFeatures?.faction !== false,
        arisewareScores: customJsonFeatures?.arisewareScores !== false,
        statsSummary: customJsonFeatures?.statsSummary !== false,
        titles: customJsonFeatures?.titles !== false,
        showNumberOfAvailableTitles: customJsonFeatures?.showNumberOfAvailableTitles === true,
        editing: customJsonFeatures?.editing !== false,
        profileLogo: customJsonFeatures?.profileLogo !== false,
        forceFallbackBackground: customJsonFeatures?.forceFallbackBackground === true,
        // Controlled with CMS implemented toggle:
        prizes: theme.metadata?.hasPrizes,
        shopButton: theme.metadata?.hasProfileShop || theme.metadata?.hasExternalShop,
    };

    const isLoading = !(
        previousUserID === profile?.userID &&
        (!featuresEnabled.arisewareScores || arisewareScores !== null) &&
        (!featuresEnabled.titles || titles !== null) &&
        (!featuresEnabled.character || characterIsLoaded) &&
        (!featuresEnabled.background || backgroundIsLoaded)
    );

    useEffect(() => {
        if (isProfileOpen) {
            let areRequestsStale = false; // To stop stale request issues
            try {
                if (previousUserID !== profile?.userID) {
                    // If is a different user, clear the old data
                    setArisewareScores(null);
                    setTitles(null);
                }
                featuresEnabled.arisewareScores &&
                    profile?.userID &&
                    getArisewareScoresForUser(profile?.userID)
                        .then((result) => {
                            if (!areRequestsStale) {
                                setArisewareScores(result);
                            }
                        })
                        .catch(() => {
                            if (!areRequestsStale) {
                                setArisewareScores([]);
                            }
                        });

                featuresEnabled.titles &&
                    getTitles(profile?.userID, isFirstTimeOpened.current)
                        .then((result) => {
                            if (!areRequestsStale) {
                                const processedTitles = processTitles(result);
                                setTitles(processedTitles);
                            }
                        })
                        .catch(() => {
                            if (!areRequestsStale) {
                                setTitles([]);
                            }
                        });

                if (
                    featuresEnabled.titles &&
                    featuresEnabled.showNumberOfAvailableTitles &&
                    numberOfAvailableTitles === null
                ) {
                    getAvailableTitles()
                        .then((result) => {
                            if (Array.isArray(result) && result.length > 0) {
                                setNumberOfAvailableTitles(result.length);
                            }
                        })
                        .catch(() => {
                            setNumberOfAvailableTitles(null);
                        });
                }

                setPreviousUserID(profile?.userID);
                isFirstTimeOpened.current = false;
            } catch (error) {}
            return () => {
                // Drawer closed, or ID changed, so set all requests as stale
                areRequestsStale = true;
            };
        }
    }, [isProfileOpen, profile?.userID, profile?.title?.title]);

    useEffect(() => {
        if (isProfileOpen) {
            audio?.play('gust-open');
            sendEventV2('profile_viewed');
        }
        setIsEditing(false);
    }, [isProfileOpen]);

    // Move focus to drawer when opened
    function onTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
        if (isProfileOpen && e.target === e.currentTarget) {
            setTimeout(() => {
                drawerRef.current?.focus();
            }, 200);
        }
    }

    return (
        <div
            role="dialog"
            aria-label="Profile"
            ref={drawerRef}
            tabIndex={-1}
            className={classNames(styles.ProfileDrawer, {
                [styles['isOpen']]: isProfileOpen,
                [styles.isLoaded]: !isLoading,
            })}
            inert={!isProfileOpen ? '' : undefined}
            onTransitionEnd={onTransitionEnd}
        >
            <div className={styles.topBar} inert={isMyPrizesOpen ? '' : undefined}>
                <IconButton
                    iconType="close"
                    buttonStyle="round"
                    ariaLabel="Close Profile"
                    className={styles.closeButton}
                    onClick={() => {
                        audio?.play('gust-close');
                        dispatch(closeProfile());
                    }}
                />
                {featuresEnabled.shopButton && (
                    <IconButton
                        buttonStyle="round"
                        iconType={theme.customJSON.shop?.shopButtonIcon ?? 'shop'}
                        title="Open Shop"
                        ariaLabel="Open Shop"
                        onClick={() => {
                            dispatch(setIsShopOpen(true));
                            sendEvent('open_shop_from_profile_header');
                        }}
                        className={styles.shopButton}
                    />
                )}
            </div>
            <div
                className={classNames(styles.content, { [styles.isEditing]: isEditing })}
                inert={isLoading || isMyPrizesOpen ? '' : undefined}
            >
                <div className={styles.profileCharacterContainer}>
                    {featuresEnabled.background && (
                        <ProfileBackground
                            classname={styles.profileBackground}
                            onLoad={() => setBackgroundIsLoaded(true)}
                            rivUrl={featuresEnabled.forceFallbackBackground ? null : profile?.background?.rivFileURL}
                            imagePath={featuresEnabled.forceFallbackBackground ? null : profile?.background?.imagePath}
                        />
                    )}
                    {featuresEnabled.profileLogo && theme.customJSON?.profileLogoUrl && (
                        <div
                            className={styles.profileLogo}
                            style={{ backgroundImage: `url("${theme.customJSON.profileLogoUrl}")` }}
                        ></div>
                    )}
                    {featuresEnabled.character && profile && (
                        <ProfileCharacter
                            classname={styles.profileCharacter}
                            onLoad={() => setCharacterIsLoaded(true)}
                            rivUrl={profile.character?.rivFileURL}
                            imagePath={profile.character?.imagePath}
                            hexColour={profile.background?.colour}
                        />
                    )}
                    {featuresEnabled.faction && profile && (
                        <Rank
                            key={profile.userID}
                            classname={styles.factionImage}
                            faction={profile.faction}
                            rank={profile.supplyRunRank}
                        />
                    )}
                    {featuresEnabled.editing && isViewingOwnProfile && (
                        <IconButton
                            iconType={isEditing ? 'priority' : 'edit'}
                            buttonStyle="round"
                            ariaLabel={isEditing ? 'Stop Editing profile' : 'Edit Profile'}
                            className={styles.editButton}
                            onClick={() => {
                                setIsEditing(!isEditing);
                            }}
                        />
                    )}
                    {featuresEnabled.layeredAvatar && profile && (
                        <AvatarLayered
                            classname={styles.editingAvatar}
                            backgroundImagePath={profile.background?.thumbnailPath}
                            characterImagePath={profile.character?.thumbnailPath}
                            frameImagePath={profile.avatarFrame?.imagePath}
                            title={`${profile.displayName}'s avatar`}
                        />
                    )}
                </div>
                <div
                    className={classNames(styles.bottomContent, { [styles.isEditing]: isEditing })}
                    inert={isEditing ? '' : undefined}
                >
                    {profile && (
                        <ThemedMenuBar classname={styles.themedBar}>
                            <div className={styles.themedBarContent}>
                                {featuresEnabled.layeredAvatar && (
                                    <AvatarLayered
                                        classname={styles.avatar}
                                        backgroundImagePath={profile.background?.thumbnailPath}
                                        characterImagePath={profile.character?.thumbnailPath}
                                        frameImagePath={profile.avatarFrame?.imagePath}
                                        title={`${profile.displayName}'s avatar`}
                                    />
                                )}
                                <DisplayNameWithBadges profile={profile} hasFaction={false} />
                            </div>
                        </ThemedMenuBar>
                    )}
                    {featuresEnabled.statsSummary && profile && (
                        <ProfileStatsSummary
                            key={profile.userID}
                            numberOfTitles={titles?.length}
                            puzzlesCompleted={
                                arisewareScores?.length
                                    ? arisewareScores?.filter((score) => score.isCompleted)?.length
                                    : null
                            }
                            userID={profile.userID}
                            role={profile.role}
                        />
                    )}
                    {featuresEnabled.prizes && isViewingOwnProfile && (
                        <div
                            className={classNames(styles.myPrizes, {
                                [styles.noBackgroundImage]: !prizesBackgroundImage,
                            })}
                            style={prizesBackgroundImage ? { backgroundImage: `url("${prizesBackgroundImage}")` } : {}}
                        >
                            <Icon type="action" />
                            <span>My Prizes</span>
                            <Button onClick={() => dispatch(setIsMyPrizesOpen(true))}>View My Prizes</Button>
                        </div>
                    )}
                    {featuresEnabled.arisewareScores && <ArisewareScores arisewareScores={arisewareScores} />}
                    {featuresEnabled.titles && (
                        <div className={styles.titlesUnlocked}>
                            <ProfileStatSummaryItem
                                classname={styles.titleStat}
                                iconType="crown"
                                title={numberOfAvailableTitles ? 'Titles Earned:' : 'Titles:'}
                                value={`${titles?.length}`}
                                suffixValue={numberOfAvailableTitles ? `/${numberOfAvailableTitles}` : null}
                            />
                            <ul>
                                {titles?.map((title: Title, index: number) => (
                                    <li key={index}>
                                        <Badge
                                            text={title.title}
                                            rarity={title.rarity}
                                            title={title.rarity?.toString()}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                {isEditing && <ProfileEditor />}
                <LoadingScreen show={isLoading} classname={styles.loadingScreen} />
            </div>
            {featuresEnabled.prizes && <MyPrizesDrawer />}
        </div>
    );
}
